import React, { useEffect, useState } from "react";
import { create, update } from "../../../constants/apiRoutes";
import Swal from "sweetalert2";

const UserRegisterForm = ({ fetchUsersData, editData, resetEditData }) => {
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    role: "",
  });
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [roles] = useState([
    { id: 0, role: "User" },
    { id: 1, role: "Admin" },
  ]);

  useEffect(() => {
    if (editData) {
      setUserForm({
        email: editData.userName,
        role: editData.roles[0],
      });
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserForm({ ...userForm, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const handleUpdateUsers = async () => {
      try {
        let postData = {
          userName: userForm.email,
          roles: userForm.role,
        };

         await update(`Account/users/${editData.id}`, postData);

        fetchUsersData();
        resetForm();
        setError("");

        Swal.fire({
          icon: "success",
          title: "User Credentials Updated Successfully",
          showConfirmButton: true,
          timer: 2500,
        });
      } catch (error) {
        console.error("Failed to update data:", error);
      }
    };

    const handleCreateUser = async () => {
      try {
        let postData = {
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
        };
         await create(`Account/register`, postData);
        fetchUsersData();
        resetForm();
        setError("");
        Swal.fire({
          icon: "success",
          title: "User Credentials added successfully",
          showConfirmButton: false,
          timer: 2500,
        });
      } catch (error) {
        console.error("Error adding recipient", error);
        if (error.response && error.response.status === 400) {
          const errorDetails = error.response.data;

          const errorMessages = errorDetails
            .map((err) => err.description)
            .join("\n");

          Swal.fire({
            icon: "error",
            title: "Failed to add User",
            html: `<pre>${errorMessages}</pre>`,
            showConfirmButton: true,
          });
        }
      }
    };

    if (editData) {
      return handleUpdateUsers();
    } else {
      return handleCreateUser();
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setUserForm({
      email: "",
      password: "",
      role: "",
    });
    resetEditData(null);
  };

  return (
    <div>
      <h2 className="text-sm font-bold text-gray-700 w-fit mb-4 p-2 rounded-lg">
        {isEditing ? "Update User" : "Add User"}
      </h2>
      <form
        onSubmit={(e) => handleSubmit(e)}
        className="p-4 rounded-xl border-2 border-gray-200"
      >
        <div
          className={`${
            editData ? "mb-12" : "mb-3"
          } flex flex-row gap-4 w-full`}
        >
          <label className="input border-none focus:outline-none outline-none flex items-center h-9 gap-4 w-1/2 bg-gray-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="h-4 w-4 opacity-70"
            >
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
            </svg>
            <input
              name="email"
              type="email"
              value={userForm.email}
              onChange={(e) => handleChange(e)}
              required
              className="grow"
              placeholder="Username"
            />
          </label>

          <label className="px-4 border-none outline-none focus:outline-none rounded-lg flex items-center h-9 gap-4 w-1/2 bg-gray-200">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_141_36)">
                <path
                  d="M12.5 17.5H10.8333C10.3913 17.5 9.96738 17.3244 9.65482 17.0118C9.34226 16.6993 9.16667 16.2754 9.16667 15.8333C9.16667 15.3913 9.34226 14.9674 9.65482 14.6548C9.96738 14.3423 10.3913 14.1667 10.8333 14.1667H12.5V12.5H10.8333C9.94928 12.5 9.10143 12.8512 8.47631 13.4763C7.85119 14.1014 7.5 14.9493 7.5 15.8333C7.5 16.7174 7.85119 17.5652 8.47631 18.1904C9.10143 18.8155 9.94928 19.1667 10.8333 19.1667H12.5V17.5ZM19.1667 15.8333C19.1667 16.7174 18.8155 17.5652 18.1904 18.1904C17.5652 18.8155 16.7174 19.1667 15.8333 19.1667H14.1667V17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333C17.5 15.3913 17.3244 14.9674 17.0118 14.6548C16.6993 14.3423 16.2754 14.1667 15.8333 14.1667H14.1667V12.5H15.8333C16.7174 12.5 17.5652 12.8512 18.1904 13.4763C18.8155 14.1014 19.1667 14.9493 19.1667 15.8333Z"
                  fill="#808797"
                />
                <path
                  d="M11.6667 15.0002H15V16.6668H11.6667V15.0002ZM5.83333 15.8335C5.83501 15.5541 5.86009 15.2754 5.90833 15.0002H2.5V13.8335C2.5 12.1668 5.83333 11.2502 7.5 11.2502C7.87716 11.2552 8.25333 11.2901 8.625 11.3543C9.31096 11.0124 10.0669 10.8341 10.8333 10.8335H15V3.3335C14.9987 2.89188 14.8227 2.46872 14.5104 2.15645C14.1981 1.84418 13.7749 1.66817 13.3333 1.66683H1.66667C1.22499 1.66817 0.801825 1.84418 0.489554 2.15645C0.177283 2.46872 0.00127411 2.89188 0 3.3335V15.8335C0.00127411 16.2751 0.177283 16.6983 0.489554 17.0105C0.801825 17.3228 1.22499 17.4988 1.66667 17.5002H10.0417C9.7508 17.0796 9.56279 16.5987 9.49992 16.0963C9.47861 15.9282 9.46888 15.759 9.47075 15.5897C8.54686 15.2537 7.50983 15.056 6.45833 15.0002H5.83333ZM3.33333 5.00016H11.6667V6.66683H3.33333V5.00016ZM3.33333 8.3335H10V10.0002H3.33333V8.3335ZM3.33333 11.6668H7.5V13.3335H3.33333V11.6668Z"
                  fill="#808797"
                />
              </g>
              <defs>
                <clipPath id="clip0_141_36">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <select
              name="role"
              value={userForm.role}
              onChange={(e) => handleChange(e)}
              className="bg-gray-200 h-full w-full focus:outline-none outline-none border-none"
            >
              <option value="" disabled hidden>
                Choose Role
              </option>
              {roles.map((role) => (
                <option key={role.id} value={role.role}>
                  {role.role}
                </option>
              ))}
            </select>
          </label>
        </div>
        {!editData && (
          <div className="flex flex-row gap-4 w-full">
            <label className="input border-none focus:outline-none outline-none flex items-center h-9 gap-4 w-1/2 bg-gray-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="h-4 w-4 opacity-70"
              >
                <path
                  fillRule="evenodd"
                  d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                name="password"
                type="password"
                placeholder="Password"
                value={userForm.password}
                onChange={(e) => handleChange(e)}
                required
                className="grow"
              />
            </label>
          </div>
        )}
        <div className="flex w-full gap-2 justify-end">
          <button
            type="submit"
            className="text-xs bg-yellow-700 text-white w-24 h-9 rounded-lg"
          >
            {isEditing ? "Update" : "Add"}
          </button>
          <button
            onClick={() => resetForm()}
            type="reset"
            className="text-xs bg-black text-white w-24 h-9 rounded-lg"
          >
            Cancel
          </button>
        </div>
        {error && (
          <div className="text-red-500 mt-2">
            <p>{error}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default UserRegisterForm;
